import { useState, useEffect } from "react";
import { useApp } from "../context/AppContext";
import ServerAssignmentModal from "./ServerAssignmentModal";
import PromptLibraryModal from "./PromptLibraryModal";


const API_BASE = import.meta.env.VITE_API_URL || "";

function formatModelOption(model) {
  return `${model.displayName} (${model.name})`;
}

export default function CreateCustomModelModal({ models, modelToEdit, onClose, onSuccess }) {
  const { showToast, trackEvent } = useApp();
  const [loading, setLoading] = useState(false);

  // Form State
  const [name, setName] = useState("");
  const [baseProvider, setBaseProvider] = useState("");
  const [baseModelId, setBaseModelId] = useState("");
  const [systemPrompt, setSystemPrompt] = useState("");
  const [showPromptLibrary, setShowPromptLibrary] = useState(false);

  // Search state
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Capabilities
  const [capabilities, setCapabilities] = useState({
    supportsStreaming: true,
    supportsVision: false,
    supportsImage: false,
    supportsVideo: false,
    supportsChat: true,
  });

  const isEdit = !!modelToEdit;



  // Server assignment state (optional)
  const [createdModelId, setCreatedModelId] = useState(null);
  const [createdModelDisplayName, setCreatedModelDisplayName] = useState("");
  const [serverAssignOpen, setServerAssignOpen] = useState(false);
  const [guilds, setGuilds] = useState(null);
  const [assignments, setAssignments] = useState(null);
  const [loadingGuilds, setLoadingGuilds] = useState(false);

  useEffect(() => {
    if (modelToEdit) {
      setName(modelToEdit.displayName);
      setBaseProvider(modelToEdit.provider);
      // If we saved baseModelId separately in backend, use it. 
      // Otherwise, assume the edit target's model ID is the custom one, 
      // but we need the original base to inherit/change from.
      // For now, let's assume we can't change base model on edit easily without extra backend support,
      // or we just default to what we have.
      // We'll set the ID to the *current* model ID as a placeholder for selection if available.
      setBaseModelId(modelToEdit.baseModelId || modelToEdit.name);
      setSystemPrompt(modelToEdit.systemPrompt || "");
      setCapabilities({
        supportsStreaming: modelToEdit.supportsStreaming,
        supportsVision: modelToEdit.supportsVision,
        supportsImage: modelToEdit.supportsImage,
        supportsVideo: modelToEdit.supportsVideo,
        supportsChat: modelToEdit.supportsChat,
      });
    }
  }, [modelToEdit]);

  // When base model changes, inherit capabilities (only in Create mode)
  useEffect(() => {
    if (isEdit) return; // Don't overwrite on edit
    if (!baseProvider || !baseModelId) return;

    const providerGroup = models?.find(p => p.provider === baseProvider);
    const model = providerGroup?.models?.find(m => m.name === baseModelId);

    if (model) {
      setCapabilities({
        supportsStreaming: model.supportsStreaming,
        supportsVision: model.supportsVision,
        supportsImage: model.supportsImage,
        supportsVideo: model.supportsVideo,
        supportsChat: model.supportsChat,
      });
    }
  }, [baseProvider, baseModelId, models, isEdit]);

  // Auto-generate slug from name

  async function handleSubmit() {
    if (!name.trim()) return showToast("Model name is required", "error");
    if (!baseProvider || !baseModelId) return showToast("Please select a base model", "error");

    setLoading(true);
    const endpoint = isEdit
      ? `${API_BASE}/api/models/custom/${modelToEdit.id}`
      : `${API_BASE}/api/models/custom`;

    // Use PUT or PATCH for edit, POST for create
    const method = isEdit ? "PUT" : "POST";

    const payload = {
      display_name: name,
      provider: baseProvider,
      base_model_id: baseModelId,
      system_prompt: systemPrompt,
      capabilities: capabilities
    };

    try {
      const res = await fetch(endpoint, {
        method,
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to save model");
      }

      const data = await res.json().catch(() => ({}));

      showToast(`Custom model ${isEdit ? "updated" : "created"}!`, "success");
      trackEvent(isEdit ? "custom_model_update" : "custom_model_create", { provider: baseProvider });

      if (!isEdit) {
        // Enable optional server mention binding UI after creation
        if (data?.id) setCreatedModelId(data.id);
        setCreatedModelDisplayName(name);
      }

      onSuccess();
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setLoading(false);
    }
  }


  function openServerAssign() {
    if (!createdModelId) return;
    setServerAssignOpen(true);
    if (!guilds) {
      setLoadingGuilds(true);
      fetch(`${API_BASE}/api/servers`, { credentials: "include" })
        .then((r) => {
          if (r.status === 401) throw new Error("Discord session expired. Re-login required.");
          if (!r.ok) throw new Error(`Discord API Error: ${r.status}`);
          return r.json();
        })
        .then((data) => {
          if (data.error) throw new Error(data.error);
          setGuilds(data.guilds || []);
          setAssignments(data.assignments || {});
        })
        .catch((err) => showToast(err.message, "error"))
        .finally(() => setLoadingGuilds(false));
    }
  }
  // Prepare flat list for the searchable dropdown
  const flatModels = models?.flatMap(pg =>
    (pg.models || [])
      .filter(m => !m.isCustom)
      .map(m => ({ ...m, provider: pg.provider }))
  ) || [];

  const filteredModels = flatModels.filter(m => {
    const term = searchTerm.trim().toLowerCase();
    const name = m.name || "";
    const display = m.displayName || "";
    const provider = m.provider || "";
    return (
      display.toLowerCase().includes(term) ||
      name.toLowerCase().includes(term) ||
      provider.toLowerCase().includes(term)
    );
  });

  // Reset highlight when search changes
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchTerm]);

  const handleKeyDown = (e) => {
    if (!searchOpen) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex(prev => (prev < filteredModels.length - 1 ? prev + 1 : prev));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex(prev => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === "Enter" && highlightedIndex >= 0) {
      e.preventDefault();
      const m = filteredModels[highlightedIndex];
      if (m) {
        setBaseProvider(m.provider);
        setBaseModelId(m.name);
        setSearchOpen(false);
        setSearchTerm("");
      }
    } else if (e.key === "Escape") {
      setSearchOpen(false);
    }
  };

  const getDisplayValue = () => {
    if (searchOpen) return searchTerm;
    if (baseModelId) {
      const found = flatModels.find(m => m.name === baseModelId && m.provider === baseProvider);
      if (found) return `${found.displayName} (${found.provider})`;
      return baseModelId;
    }
    return "";
  };

  function handleInsertPrompt(text) {
    setSystemPrompt(prev =>
      (prev || "").trim() ? (prev.trim() + "\n\n" + text) : text
    );
    setShowPromptLibrary(false);
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? "Edit Custom Model" : "Create Custom Model"}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Name</label>
            <input
              className="input"
              placeholder="e.g. Coding Assistant, Storyteller..."
              value={name}
              onChange={e => setName(e.target.value)}
            />
          </div>

          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <label className="form-label" style={{ marginBottom: 0 }}>System Prompt</label>
              <button
                type="button"
                className="btn btn-secondary"
                style={{ padding: "0.25rem 0.75rem", fontSize: "0.75rem", borderRadius: "0.5rem" }}
                onClick={() => setShowPromptLibrary(true)}
              >
                Prompt Library
              </button>
            </div>
            <textarea
              className="input"
              rows={5}
              placeholder="You are a helpful assistant..."
              value={systemPrompt}
              onChange={e => setSystemPrompt(e.target.value)}
              style={{ resize: "vertical", fontFamily: "inherit" }}
            />
            <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: "0.5rem" }}>
              This prompt will define the personality and behavior of your custom model.
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Base Model</label>
            <div className="model-selector-wrapper">
              <input
                className="model-selector-input"
                placeholder={baseModelId ? "Select a model..." : "Search for a model (e.g. GPT, Claude)..."}
                value={getDisplayValue()}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setSearchOpen(true);
                }}
                onKeyDown={handleKeyDown}
                onClick={() => {
                  setSearchOpen(true);
                  setSearchTerm("");
                }}
                onBlur={() => setTimeout(() => setSearchOpen(false), 200)}
              />
              <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-muted)' }}>▼</div>

              {searchOpen && (
                <div className="model-selector-dropdown">
                  {filteredModels.length === 0 ? (
                    <div style={{ padding: "0.75rem", color: "var(--text-muted)", fontSize: "0.875rem" }}>
                      No models found
                    </div>
                  ) : (
                    filteredModels.map((m, idx) => (
                      <div
                        key={`${m.provider}-${m.name}`}
                        className={`model-selector-option ${idx === highlightedIndex ? "focused" : ""}`}
                        onMouseDown={() => {
                          setBaseProvider(m.provider);
                          setBaseModelId(m.name);
                          setSearchOpen(false);
                          setSearchTerm("");
                        }}
                      >
                        <span className="model-selector-badge">{m.provider}</span>
                        <span>{m.displayName}</span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Capabilities</label>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: "0.75rem" }}>
              {Object.entries(capabilities).map(([key, val]) => (
                <label key={key} style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.875rem", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={val}
                    onChange={e => setCapabilities(prev => ({ ...prev, [key]: e.target.checked }))}
                  />
                  {key.replace("supports", "").replace(/([A-Z])/g, " $1").trim()}
                </label>
              ))}
            </div>
          </div>

          {/* Optional: Server @mentions binding (available after model is created) */}
          {!isEdit && createdModelId && (
            <div className="form-group" style={{ marginTop: "1rem", marginBottom: "0.5rem" }}>
              <div className="model-mentions-row">
                <div className="model-mentions-row-left">
                  <span className="model-mentions-row-at">@</span>
                  <span className="model-mentions-row-label">Mentions</span>
                </div>
                <div className="model-mentions-row-right">
                  <span className="model-mentions-row-empty">Not set</span>
                  <button
                    type="button"
                    className="model-mentions-row-manage"
                    onClick={openServerAssign}
                    aria-label="Manage servers for @mentions"
                  >
                    Manage
                  </button>
                </div>
              </div>
              <div className="model-mentions-helper">
                Other members will use this model when they @mention the bot. /chat stays personal.
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={loading}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
            {loading ? "Saving..." : (isEdit ? "Save Changes" : "Create Model")}
          </button>
        </div>
      </div>

      {serverAssignOpen && createdModelId && (
        <ServerAssignmentModal
          model={{ id: createdModelId, displayName: createdModelDisplayName }}
          guilds={guilds}
          assignments={assignments}
          setAssignments={setAssignments}
          loading={loadingGuilds}
          onClose={() => setServerAssignOpen(false)}
          onChanged={() => { }}
        />
      )}
      {showPromptLibrary && (
        <div style={{ position: 'relative', zIndex: 3000 }}>
          <PromptLibraryModal
            onClose={() => setShowPromptLibrary(false)}
            onInsert={handleInsertPrompt}
          />
        </div>
      )}
    </div >
  );
}
